import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  createDisponibilite,
  fetchDoctorSlotsByDate,
  markRangeUnavailable,
  markRangeAvailable,
  deleteDisponibilite,
  updateDisponibilite,
  fetchBlockedSlots,
} from '../redux/thunks/disponibilityThunks';
import {
  selectDoctorSlotsByDate,
  selectDisponibilityLoading,
  selectDisponibilityError,
  selectDisponibilitySuccessMessage,
  selectAllDisponibilitySlots,
} from '../redux/selectors/disponibilitySelectors';
import {
  clearDisponibilityError,
  clearDisponibilitySuccess,
} from '../redux/slices/disponibilitySlice';

export default function useDisponibility({ medecinId, date }) {
  const dispatch = useDispatch();

  const normalizedDate =
    date instanceof Date ? format(date, 'yyyy-MM-dd') : date;

  const slots = useSelector(
    normalizedDate ? selectDoctorSlotsByDate(normalizedDate) : () => []
  );
  const allSlots      = useSelector(selectAllDisponibilitySlots);
  const loadingSlots  = useSelector(selectDisponibilityLoading);
  const firstError    = useSelector(selectDisponibilityError);
  const successCreate = useSelector(selectDisponibilitySuccessMessage);

  const loadSlots = useCallback(
    (doctorIdArg, dateArg) => {
      const doctorId = doctorIdArg || medecinId;
      const finalDate =
        dateArg instanceof Date
          ? format(dateArg, 'yyyy-MM-dd')
          : dateArg || normalizedDate;
      if (!doctorId || !finalDate) return;
      return dispatch(fetchDoctorSlotsByDate({ medecinId: doctorId, date: finalDate }));
    },
    [dispatch, medecinId, normalizedDate]
  );

  const loadBlockedSlots = useCallback(
    (doctorIdArg, dateArg) => {
      const doctorId = doctorIdArg || medecinId;
      const finalDate =
        dateArg instanceof Date
          ? format(dateArg, 'yyyy-MM-dd')
          : dateArg || normalizedDate;
      if (!doctorId || !finalDate) return Promise.resolve({ payload: { data: { slots: [] } } });
      return dispatch(fetchBlockedSlots({ medecinId: doctorId, date: finalDate }));
    },
    [dispatch, medecinId, normalizedDate]
  );

  // useEffect(() => {
  //   if (medecinId && normalizedDate) {
  //     loadSlots(medecinId, normalizedDate);
  //   }
  // }, [medecinId, normalizedDate, loadSlots]);

  useEffect(() => {
  if (medecinId && normalizedDate) {
    loadSlots(medecinId, normalizedDate);
  }
}, [medecinId, normalizedDate]);
  const createFromPeriod = useCallback(
  async (doctorIdArg, period) => {
    const doctorId = doctorIdArg || medecinId;
    if (!doctorId || !period) return;

    const payload = {
      periodStart:
        period.periodStart ||
        (period.startDate instanceof Date
          ? format(period.startDate, 'yyyy-MM-dd')
          : period.startDate),
      periodEnd:
        period.periodEnd ||
        (period.endDate instanceof Date
          ? format(period.endDate, 'yyyy-MM-dd')
          : period.endDate),
      slotDuration: Number(period.slotDuration || 30),
      isAvailable: true,
      weeklySchedule: (period.weeklySchedule || [])
        .filter((day) => day.available)
        .map((day) => ({
          dayOfWeek: day.dayOfWeek,
          isAvailable: true,
          timeRanges: (day.slots || []).map((slot) => ({
            startTime: slot.startTime,
            endTime: slot.endTime,
          })),
        })),
    };

    const result = await dispatch(createDisponibilite({ medecinId: doctorId, payload }));

    if (createDisponibilite.rejected.match(result)) {
      const err = new Error(result.payload?.message || 'Erreur lors de la création');
      err.conflictingSlot = result.payload?.conflictingSlot || null;
      err.code = result.payload?.error || 'UNKNOWN';
      err.statusCode = result.payload?.statusCode || null;
      throw err;
    }

    return result;
  },
  [dispatch, medecinId]
);

  const updateSlot = useCallback(
    (slotId, payload) => {
      if (!slotId || !payload) return;
      return dispatch(updateDisponibilite({ slotId, payload }));
    },
    [dispatch]
  );

  const deleteSlot = useCallback(
    (slotId) => {
      if (!slotId) return;
      return dispatch(deleteDisponibilite({ slotId }));
    },
    [dispatch]
  );

  const blockRange = useCallback(
    async ({ startTime, endTime, reason, slotDuration }) => {
      if (!startTime || !endTime) {
        throw new Error('startTime et endTime sont requis.');
      }
      if (!medecinId) {
        throw new Error('medecinId manquant.');
      }


      const result = await dispatch(
        markRangeUnavailable({ medecinId, startTime, endTime, slotDuration })
      );

      if (markRangeUnavailable.rejected.match(result)) {
        throw new Error(result.payload || 'Erreur lors du blocage');
      }
      return result;
    },
    [dispatch, medecinId]
  );

  // ✅ unblockRange — manquait dans le fichier précédent
  const unblockRange = useCallback(
    async ({ startTime, endTime }) => {
      if (!startTime || !endTime) {
        throw new Error('startTime et endTime sont requis pour débloquer.');
      }

      const result = await dispatch(
        markRangeAvailable({ startTime, endTime })
      );

      if (markRangeAvailable.rejected.match(result)) {
        throw new Error(result.payload || 'Erreur lors du déblocage');
      }
      return result;
    },
    [dispatch]
  );

  const clearAllErrors  = useCallback(() => dispatch(clearDisponibilityError()),  [dispatch]);
  const clearAllSuccess = useCallback(() => dispatch(clearDisponibilitySuccess()), [dispatch]);

  return {
    slots,
    allSlots,
    loadingSlots,
    loadingCreate: loadingSlots,
    firstError,
    successCreate,
    createFromPeriod,
    updateSlot,
    deleteSlot,
    blockRange,
    unblockRange,  // ✅ ajouté
    loadSlots,
    loadBlockedSlots,
    clearAllErrors,
    clearAllSuccess,
  };
}